import { Injectable } from '@angular/core';
import { CanDeactivate, UrlTree } from '@angular/router';
import { map, Observable, of, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DraftExitConfirmation } from 'src/app/core/interfaces/safe-data.interface';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from 'src/app/shared/modals/confirmation-dialog/confirmation-dialog.component';
import {
  UnsavedChangesDialogComponent,
  UnsavedChangesDialogResult,
} from 'src/app/shared/modals/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { dialogWindowConfig } from 'src/app/core/constants/dialog-window-config';

@Injectable({
  providedIn: 'root',
})
export class ExitConfirmationGuard implements CanDeactivate<DraftExitConfirmation> {
  private readonly smallDialogConfig = dialogWindowConfig.small;

  constructor(private dialogMaterial: MatDialog) {}

  public canDeactivate(component: DraftExitConfirmation): Observable<boolean | UrlTree> {
    if (!component.isUnsavedData) {
      return of(true);
    }

    if (component.saveDraftForExit) {
      return this.handleRoundDraftExit(component);
    }

    if (component.enableDraftSaving) {
      return this.handleDraftExit(component);
    }

    return this.handleUnsavedExit(component);
  }

  private handleRoundDraftExit(component: DraftExitConfirmation): Observable<boolean> {
    const dialogRef = this.dialogMaterial.open(UnsavedChangesDialogComponent, {
      minWidth: dialogWindowConfig.medium.minWidth,
      width: dialogWindowConfig.medium.width,
      maxWidth: '560px',
      panelClass: 'unsaved-changes-dialog-panel',
    });

    return dialogRef.afterClosed().pipe(
      switchMap((result?: UnsavedChangesDialogResult) => {
        if (result === 'saveDraft') {
          return component.saveDraftForExit?.() ?? of(false);
        }

        return of(result === 'discard');
      })
    );
  }

  private handleDraftExit(component: DraftExitConfirmation): Observable<boolean> {
    const dialogRef = this.dialogMaterial.open(ConfirmationDialogComponent, {
      ...this.smallDialogConfig,
      data: {
        message: 'You have unsaved changes. Do you want to save before exiting?',
        submitText: 'Save as Draft',
        cancelText: 'Discard & Close',
      } as ConfirmationDialogData,
    });
    return dialogRef.afterClosed().pipe(
      map(result => {
        if (result === 'cancel') {
          return true;
        }

        if (result === 'submit') {
          component.saveAsDraft?.();
        }

        return false;
      })
    );
  }

  private handleUnsavedExit(component: DraftExitConfirmation): Observable<boolean> {
    const dialogRef = this.dialogMaterial.open(ConfirmationDialogComponent, {
      ...this.smallDialogConfig,
      data: {
        message: 'You have unsaved changes.<br>Are you sure you want to discard them?',
        submitText: 'Continue',
        cancelText: 'Discard & Close',
      } as ConfirmationDialogData,
    });

    return dialogRef.afterClosed().pipe(
      map(result => {
        if (result === 'submit') {
          return false;
        }

        if (result === 'cancel') {
          return true;
        }

        return false;
      })
    );
  }
}
